import type { FC } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { createSelector } from '@reduxjs/toolkit';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { _isNotBlank } from '@/littledash';

import { featuresSelector } from '@/support/Selectors';

import ReasonForChange from '@/components/Shared/Latest/ReasonForChange';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import Spinner from '@/components/UI/Spinner';

import {
  CASCADE_DELETE_CHILD_OPTIONS,
  UNCHECKED_CAGE_CASCADE_DELETE_OPTIONS,
  type CascadeDeleteChildType,
  type CascadeDeleteOptions,
  type CascadeDeleteParentType,
  type CascadeDeleteSelection,
  type CageCascadeDeleteOptions,
} from './ConfirmDelete.model';

import styles from './ConfirmDelete.module.scss';

export type { CageCascadeDeleteOptions, CascadeDeleteOptions };

interface ConfirmDeleteProps {
  onClick: (reason?: string, cascade?: CascadeDeleteSelection) => void | Promise<void>;
  title: string;
  text: string;
  btnTxt: string;
  textOverride?: string;
  showReasonForChange?: boolean;
  showCascadeDeleteOptions?: boolean;
  cascadeDeleteParent?: CascadeDeleteParentType;
}

interface FormDataProps {
  confirm: string;
  reason_for_change: string;
  cascade: CascadeDeleteSelection;
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  optInCascadeDelete: features?.opt_in_cascade_delete ?? false,
  reasonForChange: features?.reason_for_change ?? false,
}));

const ConfirmDelete: FC<ConfirmDeleteProps> = ({
  onClick,
  title,
  text,
  btnTxt,
  textOverride,
  showReasonForChange,
  showCascadeDeleteOptions,
  cascadeDeleteParent = 'animal',
}) => {
  const { optInCascadeDelete, reasonForChange } = useSelector(featureSelector);
  const dispatch = useDispatch();

  const formMethods = useForm<FormDataProps>({
    defaultValues: {
      confirm: '',
      reason_for_change: '',
      cascade: { ...UNCHECKED_CAGE_CASCADE_DELETE_OPTIONS },
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    setValue,
  } = formMethods;

  const cascade = useWatch({ control, name: 'cascade' }) ?? { ...UNCHECKED_CAGE_CASCADE_DELETE_OPTIONS };
  const isCageCascade = cascadeDeleteParent === 'cage';
  const animalsSelected = cascade.animals === true;
  const hasSelectedCascadeOption =
    animalsSelected || CASCADE_DELETE_CHILD_OPTIONS.some(([name]) => cascade[name] === true);
  const shouldShowCascadeDeleteOptions = optInCascadeDelete === true && showCascadeDeleteOptions === true;

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const setCascadeOption = (name: CascadeDeleteChildType, checked: boolean) => {
    setValue(`cascade.${name}`, checked, { shouldDirty: true, shouldValidate: true });
  };

  const setAnimalsCascadeOption = (checked: boolean) => {
    setValue('cascade.animals', checked, { shouldDirty: true, shouldValidate: true });
    if (!checked) {
      CASCADE_DELETE_CHILD_OPTIONS.forEach(([name]) => setCascadeOption(name, false));
    }
  };

  const setAllCascadeOptions = (checked: boolean) => {
    if (isCageCascade) {
      setAnimalsCascadeOption(checked);
    }
    CASCADE_DELETE_CHILD_OPTIONS.forEach(([name]) => {
      setCascadeOption(name, checked);
    });
  };

  const onSubmit = async ({ reason_for_change, cascade: cascadeOptions }: FormDataProps) => {
    const shouldPassReason = reasonForChange === true && showReasonForChange === true;
    const reason = shouldPassReason ? reason_for_change : undefined;
    try {
      if (shouldShowCascadeDeleteOptions) {
        if (isCageCascade) {
          await onClick(reason, {
            animals: cascadeOptions.animals === true,
            samples: cascadeOptions.samples,
            measurements: cascadeOptions.measurements,
            observations: cascadeOptions.observations,
            dosages: cascadeOptions.dosages,
          });
        } else {
          await onClick(reason, {
            samples: cascadeOptions.samples,
            measurements: cascadeOptions.measurements,
            observations: cascadeOptions.observations,
            dosages: cascadeOptions.dosages,
          });
        }
      } else {
        await onClick(reason);
      }
    } catch {
      // The caller reports the failure; keep the modal open so the delete can be retried.
      return;
    }
    closeModal();
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn('ui__narrow-modal', { [styles.narrowModalWide]: shouldShowCascadeDeleteOptions })}
        data-testid="confirm-delete__modal"
      >
        <div className="pb3 tc">
          <h3 className="normal f4 pb3" data-testid="confirm-delete__modal-header">
            {title}
          </h3>
          <p className="f6 pb2" data-testid="confirm-delete__modal-subheader">
            {_isNotBlank(textOverride) ? textOverride : text}
          </p>
        </div>
        <fieldset
          className={cn('ma0 bn', { ui__disabled: isSubmitting })}
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {shouldShowCascadeDeleteOptions && (
            <>
              <div className="bb b--gray mb3"></div>
              <div className="pb3" data-test-element="cascade-delete-options">
                <h4 className="f5 fw5 pb1">Also delete child items</h4>
                <p className="f6 dark-gray lh-copy pb3">
                  {isCageCascade
                    ? 'Select Animals to delete animals in these cages. Optionally select child items to delete with those animals.'
                    : 'Optionally delete child items with the animal. Webhooks will be sent for selected item types.'}
                </p>
                <div className="flex flex-wrap items-center pb1">
                  <a
                    className="dib link blue f7 mr3"
                    onClick={() => setAllCascadeOptions(true)}
                    data-testid="confirm-delete__cascade-select-all"
                  >
                    Select all
                  </a>
                  <a
                    className={cn('dib f7', {
                      'link blue': hasSelectedCascadeOption,
                      'pointer-events-none': !hasSelectedCascadeOption,
                    })}
                    onClick={() => setAllCascadeOptions(false)}
                    data-testid="confirm-delete__cascade-clear"
                  >
                    Clear selection
                  </a>
                </div>
                {isCageCascade && (
                  <div className="pt3">
                    <Checkbox
                      id="cascade-animals"
                      name="cascade.animals"
                      label="Animals"
                      labelClass="pl2"
                      testPrefix="confirm-delete__cascade-animals"
                      checked={animalsSelected}
                      onChange={(event) => setAnimalsCascadeOption(event.target.checked)}
                    />
                  </div>
                )}
                <div className={cn('pt3', 'flex', 'flex-wrap')} data-test-element="cascade-delete-item-options">
                  {CASCADE_DELETE_CHILD_OPTIONS.map(([name, label]) => (
                    <div className="w-50 pb2" key={name}>
                      <Checkbox
                        id={`cascade-${name}`}
                        name={`cascade.${name}`}
                        label={label}
                        labelClass="pl2"
                        testPrefix={`confirm-delete__cascade-${name}`}
                        checked={cascade[name] === true}
                        disabled={isCageCascade && !animalsSelected}
                        onChange={(event) => setCascadeOption(name, event.target.checked)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          <div className="pb3">
            {reasonForChange === true && showReasonForChange === true ? (
              <ReasonForChange data-testid="confirm-delete__modal-reason-for-change" isDelete />
            ) : (
              <>
                <label htmlFor="confirm" className="lh-copy" data-testid="confirm-delete__modal-label">
                  Type DELETE into the field to confirm
                </label>
                <input
                  type="text"
                  id="confirm"
                  data-testid="confirm-delete__modal-input"
                  {...register('confirm', { validate: (value: string) => value === 'DELETE' })}
                />
                <ErrorMessage
                  errors={errors}
                  name="confirm"
                  render={() => (
                    <p className="f6 red db lh-copy" data-testid="confirm-delete__modal-validation">
                      Text entered is incorrect, enter DELETE to continue.
                    </p>
                  )}
                />
              </>
            )}
          </div>
          <div>
            {isSubmitting ? (
              <div className="flex flex-column items-center pv3" data-testid="confirm-delete__modal-loading">
                <Spinner size="small" color="blue" />
              </div>
            ) : (
              <>
                <button type="submit" className="button critical w-100" data-testid="confirm-delete__modal-confirm">
                  {btnTxt}
                </button>
                <button
                  type="button"
                  className="button plain db w-100 mt2"
                  onClick={closeModal}
                  data-testid="confirm-delete__modal-cancel"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </fieldset>
      </form>
    </FormProvider>
  );
};

export default ConfirmDelete;
